import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";
import { cachedRequest } from "../../../utils/requestCache";

export interface LifecycleType {
  lifecycle_type_id: number;
  lifecycle_type_key: string;
  lifecycle_type_display_name: string;
  is_active: boolean | null;
  tenant_id: number;
}

export interface LifecycleStage {
  lifecycle_type_stage_id: number;
  stage_name: string;
  stage_order: number;
  tenant_id: number;
  lifecycle_type_id: number;
  lifecycle_type_key?: string;
}

export interface DealStatusReason {
  lifecycle_status_reason_id: number;
  reason_name: string;
  lifecycle_status_reason_category_key: string;
}

export interface LifecycleStatusReasonCategory {
  lifecycle_status_reason_category_id: number;
  lifecycle_status_reason_category_key: string;
  lifecycle_status_reason_category_display_name?: string;
}

export interface LifecycleStatusReason {
  lifecycle_status_reason_id: number;
  reason_name: string;
  lifecycle_status_reason_category_id?: number;
  is_active?: boolean;
  tenant_id?: number;
}

export interface DealEntityStatusReason {
  lifecycle_entity_status_reason_id?: number;
  record_id?: number;
  crm_entity_id?: number;
  lifecycle_status_reason_id?: number;
  lifecycle_status_reason?: {
    lifecycle_status_reason_id?: number;
    reason_name?: string;
    is_active?: boolean;
    lifecycle_status_reason_category?: {
      lifecycle_status_reason_category_id?: number;
      lifecycle_status_reason_category_key?: string;
      lifecycle_status_reason_category_display_name?: string;
    };
  };
  [key: string]: any;
}

const getDealUrl = (tenantId: number) => {
  if (!tenantId) throw new Error("Tenant ID is required.");
  return `${API_BASE_URL}/deal/?tenant_id=${tenantId}`;
};

const addTenantToDeal = <T extends { tenant_id?: number | null }>(
  deal: Partial<T>,
  tenantId: number
): Partial<T> => {
  if (!tenantId) {
    throw new Error("Tenant ID is required.");
  }

  const authResponse = localStorage.getItem("authResponse");
  let currentUserId = null;

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      currentUserId = auth.user_id;
    } catch (e) {
      console.warn("Could not parse auth response for user_id");
    }
  }

  return {
    ...deal,
    tenant_id: tenantId,
    created_by: currentUserId,
    updated_by: currentUserId,
  };
};

export interface Deal {
  deal_id?: number;
  crm_entity_id?: number;
  created_by_username?: string;
  updated_by_username?: string;
  title: string;
  created_datetime: string;
  updated_datetime: string;
  assigned_datetime?: string;
  exepected_amount?: string;
  currency_code: string;
  priority: string;
  products_n_services?: string | null;

  estimated_value?: number | null;
  grand_total?: number | null;
  source?: string;
  lifecycle_source_id?: number | null;
  lifecycle_source?: {
    lifecycle_source_id: number;
    lifecycle_source_key: string;
    lifecycle_source_display_name: string;
  };
  score?: number;
  preferred_language?: string;
  time_zone?: string;
  status?: string;
  lifecycle_type_stage_id?: number;
  assigned_user_id?: number;
  company_id?: number;
  primary_contact_id?: number;
  tenant_id?: number;
  assigned_team_id?: number;

  dealName?: string;
  stage?: string;
  dealValue?: string;
  tag1?: string;
  closeDate?: string;
  crearedDate?: string;
  owner?: string;
  expected_amount?: number;

  primary_contact?: {
    contact_id: number;
    primary_email: string;
    personal_email: string;
    primary_phone: string;
    mobile_phone: string;
    preferred_language: string;
    address_line1: string;
    address_line2: string;
    source: string;
    city: string;
    state: string;
    country: string;
    full_name: string;
    avatar_url: string;
    job_title?: string;
    tags?: {
      insert: { entity_tag_id: number; entity_tag_name: string }[];
    } | null;
  };

  company?: {
    company_id: number;
    company_name: string;
    business_type_display_name?: string;
    industry_type_display_name?: string;
    avatar_url: string;
    address: string;
  };

  assigned_user?: {
    user_id: number;
    full_name: string;
    email?: string;
    phone?: string | null;
    avatar_url: string;
  };
  lifecycle_type_stage?: {
    lifecycle_type_stage_id: number;
    stage_name: string;
    stage_order?: number;
    lifecycle_type_key?: string;
  };

  created_by?: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };

  updated_by?: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };

  tenant?: {
    tenant_id: number;
    tenant_name: string;
    domain_name: string;
    is_active: boolean;
    language: string | null;
  };
  realted_lead?: {
    lead_id: number;
    title: string;
    created_datetime: string;
    updated_datetime: string;
    lifecycle_source: {
      lifecycle_source_id: number;
      lifecycle_source_key: string;
      lifecycle_source_display_name: string;
    };
    priority: string;
    avatar_url?: string;
  };
  related_pipeline?: {
    pipeline_id: number;
    title: string;
    created_datetime: string;
    updated_datetime: string;
    priority: string;
    avatar_url?: string;
  };
  assigned_team?: {
    team_id: number;
    team_name: string;
    created_datetime?: string;
    /** if your API actually returns a logo/avatar URL, add that here */
    logo_url?: string;
  };
  is_archive?: boolean;
  lifecycle_status_reason?: DealStatusReason | null;

  [key: string]: any;
}

export interface DealRequest {
  title?: string;
  estimated_value?: number | null;
  grand_total?: number | null;
  exepected_amount?: string;
  expected_amount?: number;
  currency_code?: string;
  priority?: string;
  products_n_services?: string | null;
  source?: string;
  lifecycle_source_id?: number;
  score?: number;
  preferred_language?: string;
  time_zone?: string;
  follow_up_date?: string;
  description?: string;
  tags?: string[];
  lifecycle_type_stage_id?: number;
  lifecycle_type_key?: string;
  assigned_user_id?: number;
  company_id?: number;
  primary_contact_id?: number;
  primary_contact?: number;
  related_lead_id?: number;
  related_pipeline_id?: number;

  dealName?: string;
  stage?: string;
  dealValue?: string;
  tag1?: string;
  closeDate?: string;
  owner?: string;
  status?: string;

  created_by?: number;
  updated_by?: number;

  tenant_id?: number;
  assigned_team_id?: number;
  tags_list?: Array<{ id: number; name: string }>;
}

export interface Team {
  team_id: number;
  name: string;
  [key: string]: any;
}

class DealServiceClass {
  async getDeals(
    tenantId: number,
    page: number = 1,
    limit: number = 20,
    filters?: Record<string, any>
  ): Promise<{ results: Deal[]; count: number }> {
    try {
      let url = `${API_BASE_URL}/deal/?tenant_id=${tenantId}&page=${page}&limit=${limit}`;

      if (filters && filters.hasOwnProperty('is_archive')) {
        url += `&is_archive=${filters.is_archive}`;
      } else {
        url += `&is_archive=false`;
      }

      if (filters) {
        Object.keys(filters).forEach((key) => {
          if (["searchTerm", "dateRange", "statusFilter", "archivedFilter", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
            return;
          }
          if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
            url += `&${key}=${encodeURIComponent(filters[key])}`;
          }
        });

        if (filters.searchTerm) {
          url += `&title=${encodeURIComponent(filters.searchTerm)}`;
        }
        if (filters.dateRange && filters.dateRange[0]) {
          url += `&created_datetime_gte=${filters.dateRange[0].toISOString().split('T')[0]}`;
        }
        if (filters.dateRange && filters.dateRange[1]) {
          url += `&created_datetime_lte=${filters.dateRange[1].toISOString().split('T')[0]}`;
        }
        if (filters.statusFilter) {
          url += `&lifecycle_type_stage_id=${encodeURIComponent(filters.statusFilter)}`;
        }
      }

      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });

      return {
        results: response.data.results || response.data || [],
        count:
          response.data.count ||
          response.data.results?.length ||
          response.data?.length ||
          0,
      };
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch deals");
    }
  }

  async getDealById(id: number, tenantId: number): Promise<Deal> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/deal/${id}/?tenant_id=${tenantId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch deal");
    }
  }

  async createDeal(dealData: Partial<DealRequest>): Promise<Deal> {
    try {
      const dealWithTenant = addTenantToDeal(dealData, dealData.tenant_id || 0);
      const response = await axios.post(
        getDealUrl(dealData.tenant_id || 0),
        dealWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw Object.assign(new Error(error.response?.data?.message || "Failed to create deal"), { response: error.response });
    }
  }

  async updateDeal(id: number, dealData: Partial<DealRequest>): Promise<Deal> {
    try {
      const tenantId =
        dealData.tenant_id ||
        parseInt(localStorage.getItem("selectedTenantId") || "0", 10);

      const authResponse = localStorage.getItem("authResponse");
      let currentUserId = null;

      if (authResponse) {
        try {
          const auth = JSON.parse(authResponse);
          currentUserId = auth.user_id;
        } catch (e) {
          console.warn("Could not parse auth response for user_id");
        }
      }

      const updatePayload = {
        ...dealData,
        tenant_id: tenantId,
        updated_by: currentUserId,
      };

      delete updatePayload.created_by;
      delete updatePayload.lifecycle_type_key;

      const response = await axios.patch(
        `${API_BASE_URL}/deal/${id}/?tenant_id=${tenantId}`,
        updatePayload,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw Object.assign(new Error(error.response?.data?.message || "Failed to update deal"), { response: error.response });
    }
  }

  async deleteDeal(id: number, tenantId: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/deal/${id}/?tenant_id=${tenantId}`, {
        headers: getAuthHeaders(),
      });
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to delete deal");
    }
  }

  async getLifecycleTypes(tenantId: number): Promise<LifecycleType[]> {
    try {
      return await cachedRequest(`lifecycle-types:${tenantId}`, async () => {
        const response = await axios.get(
          `${API_BASE_URL}/lifecycle-type/?tenant_id=${tenantId}`,
          {
            headers: getAuthHeaders(),
          }
        );

        if (response.data.results) {
          return response.data.results;
        } else if (Array.isArray(response.data)) {
          return response.data;
        }

        return [];
      });
    } catch (error) {
      console.error("Error fetching lifecycle types:", error);
      return handleAuthError(error);
    }
  }

  async getLifecycleStages(lifecycleTypeId: number, tenantId: number): Promise<LifecycleStage[]> {
    try {
      return await cachedRequest(`lifecycle-stages:${lifecycleTypeId}:${tenantId}`, async () => {
        const response = await axios.get(
          `${API_BASE_URL}/batch/lifecycle-type-stage/?lifecycle_type_id=${lifecycleTypeId}&tenant_id=${tenantId}`,
          {
            headers: getAuthHeaders(),
          }
        );

        if (Array.isArray(response.data)) {
          return response.data.sort(
            (a: LifecycleStage, b: LifecycleStage) =>
              a.stage_order - b.stage_order
          );
        }

        return [];
      });
    } catch (error) {
      console.error("Error fetching lifecycle stages:", error);
      return handleAuthError(error);
    }
  }

  async getDealStages(tenantId: number): Promise<LifecycleStage[]> {
    try {
      const lifecycleTypes = await this.getLifecycleTypes(tenantId);

      const dealLifecycleType = lifecycleTypes.find(
        (type) => type.lifecycle_type_key === "DEAL"
      );

      if (!dealLifecycleType) {
        console.warn("No DEAL lifecycle type found for tenant:", tenantId);
        return [];
      }

      const stages = await this.getLifecycleStages(
        dealLifecycleType.lifecycle_type_id,
        tenantId
      );

      return stages.map((stage) => ({
        ...stage,
        lifecycle_type_key: dealLifecycleType.lifecycle_type_key,
      }));
    } catch (error) {
      console.error("Error fetching deal stages:", error);
      return handleAuthError(error);
    }
  }

  async getPipelineStages(tenantId: number): Promise<LifecycleStage[]> {
    try {
      const lifecycleTypes = await this.getLifecycleTypes(tenantId);

      const pipelineLifecycleType = lifecycleTypes.find(
        (type) => type.lifecycle_type_key === "PIPELINE"
      );

      if (!pipelineLifecycleType) {
        console.warn("No PIPELINE lifecycle type found for tenant:", tenantId);
        return [];
      }

      return await this.getLifecycleStages(
        pipelineLifecycleType.lifecycle_type_id,
        tenantId
      );
    } catch (error) {
      console.error("Error fetching pipeline stages:", error);
      return handleAuthError(error);
    }
  }

  async updateDealPriority(
    id: number,
    priority: string,
    tenantId: number
  ): Promise<Deal> {
    try {
      const updateData = {
        priority,
        tenant_id: tenantId,
      };
      const dealWithTenant = addTenantToDeal(updateData, tenantId);
      const response = await axios.patch(
        `${API_BASE_URL}/deal/${id}/?tenant_id=${tenantId}`,
        dealWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to update deal priority"
      );
    }
  }

  async updateDealStage(
    id: number,
    stageId: number,
    tenantId: number
  ): Promise<Deal> {
    try {
      const authResponse = localStorage.getItem("authResponse");
      let currentUserId = null;

      if (authResponse) {
        try {
          const auth = JSON.parse(authResponse);
          currentUserId = auth.user_id;
        } catch (e) {
          console.warn("Could not parse auth response for user_id");
        }
      }

      const updatePayload = {
        lifecycle_type_stage_id: stageId,
        tenant_id: tenantId,
        updated_by: currentUserId,
      };

      const response = await axios.patch(
        `${API_BASE_URL}/deal/${id}/?tenant_id=${tenantId}`,
        updatePayload,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to update deal stage"
      );
    }
  }

  async getTeams(tenantId: number): Promise<Team[]> {
    try {
      const url = `${API_BASE_URL}/team/?tenant_id=${tenantId}&response_mode=light`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      if (response.data.results) {
        return response.data.results;
      } else if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch teams");
    }
  }

  async getCrmEntityId(entityKey: string): Promise<number | null> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/crm-entity/?crm_entity_key=${entityKey}`,
        { headers: getAuthHeaders() }
      );
      return response.data?.results?.[0]?.crm_entity_id ?? null;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch CRM entity");
    }
  }

  async getLifecycleStatusReasonCategories(): Promise<LifecycleStatusReasonCategory[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/lifecycle-status-reason-category/`,
        { headers: getAuthHeaders() }
      );
      return response.data?.results || [];
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch status reason categories");
    }
  }

  async searchLifecycleStatusReasons(
    tenantId: number,
    options: { reasonName?: string; categoryId?: number } = {}
  ): Promise<LifecycleStatusReason[]> {
    try {
      let url = `${API_BASE_URL}/lifecycle-status-reason/?tenant_id=${tenantId}&is_active=true`;
      if (options.reasonName) {
        url += `&reason_name=${encodeURIComponent(options.reasonName)}`;
      }
      if (options.categoryId) {
        url += `&lifecycle_status_reason_category_id=${options.categoryId}`;
      }
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data?.results || [];
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch status reasons");
    }
  }

  async getDealStatusReason(
    tenantId: number,
    crmEntityId: number,
    dealId: number
  ): Promise<DealEntityStatusReason | null> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/lifecycle-entity-status-reason/?tenant_id=${tenantId}&crm_entity_id=${crmEntityId}&record_id=${dealId}`,
        { headers: getAuthHeaders() }
      );
      return response.data?.results?.[0] || null;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch deal status reason");
    }
  }

  async saveDealStatusReason(payload: {
    tenant_id: number | string;
    crm_entity_id: number;
    record_id: number;
    created_by?: number;
    updated_by?: number;
    lifecycle_status_reason_id?: number;
    is_new_reason?: string;
    new_reason_name?: string;
    lifecycle_status_reason_category_id?: number;
  }): Promise<any> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/lifecycle-entity-status-reason/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to mark deal status");
    }
  }
}

export const DealService = new DealServiceClass();
